/**
 * Demand Analytics Dashboard — /marketing/public-portal/analytics
 *
 * Tabs: Overview | Geo / PIN Code | Visitor Funnel | Event Interest | Campaign Attribution
 * UI: Prashasta premium light — warm ivory backgrounds, ruby accent, champagne gold
 * No dark panels. No raw UUIDs. No snake_case labels. No AI. No CRM data beyond LEAD_CREATED.
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  BarChart, Bar, LineChart, Line, FunnelChart, Funnel, LabelList,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import {
  TrendingUp, Users, Target, MapPin, BarChart2, Globe,
  ChevronDown, RefreshCw, Filter, ArrowUpRight, ArrowDownRight,
  Minus, Eye, Megaphone, Search,
} from "lucide-react";
import api from "@/services/api";

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  bg:          "#FBF9F7",
  card:        "#FFFFFF",
  cardBorder:  "#EDE8E3",
  sand:        "#F0E8DA",
  charcoal:    "#1C1917",
  mid:         "#57534E",
  muted:       "#A8A29E",
  ruby:        "#B91C1C",
  rubyLight:   "#FEF2F2",
  gold:        "#C4922A",
  goldLight:   "#FEF9C3",
  green:       "#15803D",
  greenLight:  "#DCFCE7",
  amber:       "#B45309",
  amberLight:  "#FEF3C7",
  chart: ["#B91C1C", "#C4922A", "#15803D", "#1D4ED8", "#7C3AED", "#0369A1"],
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n) {
  if (n === undefined || n === null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function fmtPct(n) {
  if (n === undefined || n === null) return "—";
  return `${Number(n).toFixed(1)}%`;
}

function TrendBadge({ pct }) {
  if (pct === undefined || pct === null) return null;
  const pos = pct >= 0;
  return (
    <span
      className="inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded-full"
      style={{ background: pos ? T.greenLight : T.rubyLight, color: pos ? T.green : T.ruby }}
    >
      {pos ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function StatCard({ icon: Icon, label, value, sub, trend, color, testId }) {
  return (
    <div
      className="rounded-xl p-5 flex flex-col gap-3 border"
      style={{ background: T.card, borderColor: T.cardBorder }}
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: T.sand }}>
            <Icon size={16} style={{ color: color || T.ruby }} />
          </div>
          <span className="text-sm font-medium" style={{ color: T.mid }}>{label}</span>
        </div>
        {trend !== undefined && <TrendBadge pct={trend} />}
      </div>
      <div className="flex items-end gap-2">
        <span className="text-3xl font-bold tracking-tight" style={{ color: T.charcoal }}>{value}</span>
      </div>
      {sub && <p className="text-xs" style={{ color: T.muted }}>{sub}</p>}
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <h3 className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: T.mid }}>
      {children}
    </h3>
  );
}

// ── Period Selector ───────────────────────────────────────────────────────────
const PERIODS = [
  { value: "today", label: "Today" },
  { value: "7d",    label: "Last 7 days" },
  { value: "30d",   label: "Last 30 days" },
];

function PeriodPicker({ value, onChange }) {
  return (
    <div className="flex gap-1 rounded-lg p-1 border" style={{ background: T.sand, borderColor: T.cardBorder }}>
      {PERIODS.map(p => (
        <button
          key={p.value}
          onClick={() => onChange(p.value)}
          className="px-3 py-1.5 rounded-md text-sm font-medium transition-all"
          style={value === p.value
            ? { background: T.charcoal, color: "#FFF" }
            : { background: "transparent", color: T.mid }}
          data-testid={`period-btn-${p.value}`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

// ── Data fetcher hook ─────────────────────────────────────────────────────────
function useAnalytics(tab, period) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const endpoints = {
    overview:  `/public-analytics/dashboard/overview?period=${period}`,
    geo:       `/public-analytics/dashboard/geo?period=${period}`,
    funnel:    `/public-analytics/dashboard/funnel?period=${period}`,
    interests: `/public-analytics/dashboard/interests?period=${period}`,
    campaigns: `/public-analytics/dashboard/campaigns?period=${period}`,
  };

  const fetch = useCallback(async () => {
    if (!endpoints[tab]) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(endpoints[tab]);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  }, [tab, period]); // eslint-disable-line

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refresh: fetch };
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Overview
// ═══════════════════════════════════════════════════════════════════════════════

function OverviewTab({ data }) {
  if (!data) return null;
  const d = data;

  const stats = [
    { icon: Users,     label: "Unique Visitors",       value: fmt(d.unique_visitors),         testId: "stat-visitors" },
    { icon: Eye,       label: "Sessions",               value: fmt(d.sessions),                testId: "stat-sessions" },
    { icon: Target,    label: "Check My Date",          value: fmt(d.date_check_starts),       sub: `${fmt(d.date_check_completes)} completed`, testId: "stat-date-check" },
    { icon: TrendingUp,label: "Build My Event",         value: fmt(d.build_event_starts),      sub: `${fmt(d.build_event_completes)} completed`, testId: "stat-bme" },
    { icon: Megaphone, label: "WhatsApp Planner",       value: fmt(d.whatsapp_planner_clicks), testId: "stat-wa" },
    { icon: Users,     label: "Inquiry Starts",         value: fmt(d.inquiry_starts),          sub: `${fmt(d.inquiry_submissions)} submitted`, testId: "stat-inquiry" },
    { icon: Target,    label: "Pre-Leads",              value: fmt(d.preleads),                color: T.gold, testId: "stat-preleads" },
    { icon: TrendingUp,label: "Leads Created",          value: fmt(d.leads_created),           color: T.green, testId: "stat-leads-created" },
  ];

  return (
    <div className="space-y-8">
      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <StatCard key={i} {...s} />
        ))}
      </div>

      {/* Conversion highlight */}
      <div
        className="rounded-xl p-6 border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        style={{ background: T.sand, borderColor: T.cardBorder }}
        data-testid="conversion-highlight"
      >
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: T.mid }}>
            Pre-Lead → Lead Conversion
          </p>
          <p className="text-4xl font-bold" style={{ color: T.ruby }}>
            {fmtPct(d.prelead_to_lead_conversion_pct)}
          </p>
          <p className="text-sm mt-1" style={{ color: T.mid }}>
            {fmt(d.leads_created)} leads from {fmt(d.preleads)} pre-leads
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: T.muted }}>Funnel</p>
          <p className="text-sm" style={{ color: T.mid }}>
            Visitors → Pre-Leads: {d.unique_visitors > 0 ? fmtPct(d.preleads / d.unique_visitors * 100) : "—"}
          </p>
          <p className="text-sm" style={{ color: T.mid }}>
            Visitors → Leads: {d.unique_visitors > 0 ? fmtPct(d.leads_created / d.unique_visitors * 100) : "—"}
          </p>
        </div>
      </div>

      {/* Trend chart */}
      {d.trend && d.trend.length > 0 && (
        <div>
          <SectionHeading>Daily Trend</SectionHeading>
          <div className="rounded-xl border p-4" style={{ background: T.card, borderColor: T.cardBorder }}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={d.trend} margin={{ left: 0, right: 16, top: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDE8E3" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: T.muted }} />
                <YAxis tick={{ fontSize: 11, fill: T.muted }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, borderColor: T.cardBorder }} />
                <Line type="monotone" dataKey="visitors" stroke={T.charcoal} strokeWidth={2} dot={false} name="Visitors" />
                <Line type="monotone" dataKey="preleads" stroke={T.gold} strokeWidth={2} dot={false} name="Pre-Leads" />
                <Line type="monotone" dataKey="leads"    stroke={T.ruby}  strokeWidth={2} dot={false} name="Leads" />
              </LineChart>
            </ResponsiveContainer>
            <div className="flex gap-4 justify-center mt-2">
              {[{c:T.charcoal,l:"Visitors"},{c:T.gold,l:"Pre-Leads"},{c:T.ruby,l:"Leads"}].map(({c,l}) => (
                <span key={l} className="flex items-center gap-1.5 text-xs" style={{ color: T.mid }}>
                  <span className="w-3 h-0.5 inline-block rounded" style={{ background: c }} />
                  {l}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Geo / PIN Code
// ═══════════════════════════════════════════════════════════════════════════════

function GeoTab({ data }) {
  const [search, setSearch] = useState("");
  if (!data) return null;
  const rows = (data.rows || []).filter(r =>
    !search || r.postal_code?.includes(search) || r.city?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: T.muted }} />
          <input
            className="w-full pl-8 pr-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: T.cardBorder, background: T.card, color: T.charcoal }}
            placeholder="Search postal code or city…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="geo-search-input"
          />
        </div>
        <p className="text-xs" style={{ color: T.muted }}>
          Buckets with fewer than 5 visitors are suppressed
        </p>
      </div>

      <div className="overflow-x-auto rounded-xl border" style={{ borderColor: T.cardBorder }}>
        <table className="w-full text-sm" data-testid="geo-table">
          <thead style={{ background: T.sand }}>
            <tr>
              {["Postal Code","City / Area","Visitors","Sessions","Intent Actions","Pre-Leads","Top Event Type"].map(h => (
                <th key={h} className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide" style={{ color: T.mid }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-12" style={{ color: T.muted }}>
                  No geo data yet. User-declared locations appear when visitors enter city / PIN during inquiry.
                </td>
              </tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="border-t" style={{ borderColor: T.cardBorder }}>
                <td className="px-4 py-3 font-mono font-medium" style={{ color: T.charcoal }}>{r.postal_code}</td>
                <td className="px-4 py-3" style={{ color: T.charcoal }}>{r.city || "—"}</td>
                <td className="px-4 py-3 font-semibold" style={{ color: T.ruby }}>{fmt(r.visitors)}</td>
                <td className="px-4 py-3">{fmt(r.sessions)}</td>
                <td className="px-4 py-3">{fmt(r.intent_actions)}</td>
                <td className="px-4 py-3 font-semibold" style={{ color: T.gold }}>{fmt(r.preleads)}</td>
                <td className="px-4 py-3">
                  {r.top_event_type
                    ? <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: T.sand, color: T.charcoal }}>{r.top_event_type}</span>
                    : "—"
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Visitor Funnel
// ═══════════════════════════════════════════════════════════════════════════════

function FunnelTab({ data }) {
  if (!data) return null;
  const stages = data.stages || [];

  const COLORS = [T.charcoal, T.gold, T.ruby, T.green];
  const maxCount = Math.max(...stages.map(s => s.count), 1);

  return (
    <div className="space-y-6">
      {/* Funnel bars */}
      <div className="space-y-4" data-testid="funnel-stages">
        {stages.map((s, i) => {
          const width = Math.max(12, (s.count / maxCount) * 100);
          return (
            <div key={s.stage} className="rounded-xl border p-5" style={{ background: T.card, borderColor: T.cardBorder }}>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: COLORS[i] }}>
                    {i + 1}
                  </span>
                  <span className="font-semibold text-sm" style={{ color: T.charcoal }}>{s.stage}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold" style={{ color: COLORS[i] }}>{fmt(s.count)}</span>
                  <TrendBadge pct={s.trend_pct} />
                </div>
              </div>
              <div className="h-3 rounded-full overflow-hidden" style={{ background: T.sand }}>
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${width}%`, background: COLORS[i] }}
                />
              </div>
              <div className="flex gap-4 mt-2 text-xs flex-wrap" style={{ color: T.muted }}>
                {s.conversion_from_prev_pct !== null && (
                  <span>Conversion from previous: <strong style={{ color: T.charcoal }}>{fmtPct(s.conversion_from_prev_pct)}</strong></span>
                )}
                {s.drop_off_pct !== null && (
                  <span>Drop-off: <strong style={{ color: T.ruby }}>{fmtPct(s.drop_off_pct)}</strong></span>
                )}
                <span>Previous period: <strong style={{ color: T.charcoal }}>{fmt(s.prev_count)}</strong></span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Conversion summary */}
      <div className="rounded-xl p-5 border" style={{ background: T.sand, borderColor: T.cardBorder }}>
        <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: T.mid }}>
          Conversion Summary — Unique Visitors
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {stages.slice(1).map((s, i) => (
            <div key={s.stage}>
              <p className="text-xs" style={{ color: T.muted }}>→ {s.stage}</p>
              <p className="text-xl font-bold" style={{ color: T.ruby }}>{fmtPct(s.conversion_from_prev_pct)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Event Interest
// ═══════════════════════════════════════════════════════════════════════════════

function InterestsTab({ data }) {
  if (!data) return null;
  const rows = data.event_type_interest || [];
  const scRows = data.service_category_interest || [];

  return (
    <div className="space-y-8">
      {/* Event type interest */}
      <div>
        <SectionHeading>Event Type Interest</SectionHeading>
        {rows.length === 0 ? (
          <p className="text-sm py-8 text-center" style={{ color: T.muted }}>
            No event type browsing data yet. Events appear as visitors browse the public portal.
          </p>
        ) : (
          <>
            <div className="rounded-xl border p-4 mb-4" style={{ background: T.card, borderColor: T.cardBorder }}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={rows.slice(0, 8)} margin={{ left: 0, right: 0, top: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EDE8E3" vertical={false} />
                  <XAxis dataKey="event_type" tick={{ fontSize: 11, fill: T.muted }} />
                  <YAxis tick={{ fontSize: 11, fill: T.muted }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, borderColor: T.cardBorder }} />
                  <Bar dataKey="visitors" name="Visitors" radius={[4, 4, 0, 0]}>
                    {rows.slice(0, 8).map((_, i) => (
                      <Cell key={i} fill={T.chart[i % T.chart.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="overflow-x-auto rounded-xl border" style={{ borderColor: T.cardBorder }}>
              <table className="w-full text-sm" data-testid="interest-table">
                <thead style={{ background: T.sand }}>
                  <tr>
                    {["Event Type","Visitors","Sessions","Intent Actions","Pre-Leads","Leads Created"].map(h => (
                      <th key={h} className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide" style={{ color: T.mid }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t" style={{ borderColor: T.cardBorder }}>
                      <td className="px-4 py-3 font-medium" style={{ color: T.charcoal }}>
                        <span className="px-2 py-0.5 rounded-full text-xs" style={{ background: T.sand, color: T.charcoal }}>
                          {r.event_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold" style={{ color: T.ruby }}>{fmt(r.visitors)}</td>
                      <td className="px-4 py-3">{fmt(r.sessions)}</td>
                      <td className="px-4 py-3">{fmt(r.intent_actions)}</td>
                      <td className="px-4 py-3 font-semibold" style={{ color: T.gold }}>{fmt(r.preleads)}</td>
                      <td className="px-4 py-3 font-semibold" style={{ color: T.green }}>{fmt(r.leads_created)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Service category interest */}
      {scRows.length > 0 && (
        <div>
          <SectionHeading>Service Category Browsing</SectionHeading>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {scRows.map((r, i) => (
              <div key={i} className="rounded-xl border p-4" style={{ background: T.card, borderColor: T.cardBorder }}>
                <p className="text-xs font-medium truncate mb-1" style={{ color: T.mid }}>
                  {r.service_category_id?.replace(/-/g, " ").replace(/ueim sc /i, "") || "Service"}
                </p>
                <p className="text-xl font-bold" style={{ color: T.charcoal }}>{fmt(r.visitors)}</p>
                <p className="text-xs" style={{ color: T.muted }}>{fmt(r.events)} views</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: Campaign Attribution
// ═══════════════════════════════════════════════════════════════════════════════

function CampaignsTab({ data }) {
  if (!data) return null;
  const rows = data.rows || [];

  return (
    <div className="space-y-4">
      {rows.length === 0 ? (
        <div
          className="rounded-xl p-12 text-center border"
          style={{ background: T.card, borderColor: T.cardBorder }}
        >
          <Megaphone size={32} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium mb-1" style={{ color: T.charcoal }}>No campaign data yet</p>
          <p className="text-sm" style={{ color: T.muted }}>
            Campaign data appears when visitors arrive with UTM parameters (utm_source, utm_medium, utm_campaign).
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border" style={{ borderColor: T.cardBorder }}>
          <table className="w-full text-sm" data-testid="campaigns-table">
            <thead style={{ background: T.sand }}>
              <tr>
                {["Source","Medium","Campaign","Landing Page","Visitors","Sessions","Pre-Leads","Leads","Conversion"].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide whitespace-nowrap" style={{ color: T.mid }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t" style={{ borderColor: T.cardBorder }}>
                  <td className="px-4 py-3 font-medium" style={{ color: T.charcoal }}>{r.source}</td>
                  <td className="px-4 py-3" style={{ color: T.mid }}>{r.medium}</td>
                  <td className="px-4 py-3">{r.campaign || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs" style={{ color: T.muted, maxWidth: 140 }}>
                    <span className="truncate block" title={r.landing_page}>{r.landing_page}</span>
                  </td>
                  <td className="px-4 py-3 font-semibold" style={{ color: T.ruby }}>{fmt(r.visitors)}</td>
                  <td className="px-4 py-3">{fmt(r.sessions)}</td>
                  <td className="px-4 py-3 font-semibold" style={{ color: T.gold }}>{fmt(r.preleads)}</td>
                  <td className="px-4 py-3 font-semibold" style={{ color: T.green }}>{fmt(r.leads_created)}</td>
                  <td className="px-4 py-3">
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-semibold"
                      style={{
                        background: r.prelead_to_lead_conversion_pct > 20 ? T.greenLight : T.sand,
                        color: r.prelead_to_lead_conversion_pct > 20 ? T.green : T.charcoal,
                      }}
                    >
                      {fmtPct(r.prelead_to_lead_conversion_pct)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════

const TABS = [
  { id: "overview",  label: "Overview",           icon: BarChart2 },
  { id: "geo",       label: "Geo / PIN Code",      icon: MapPin },
  { id: "funnel",    label: "Visitor Funnel",      icon: TrendingUp },
  { id: "interests", label: "Event Interest",      icon: Target },
  { id: "campaigns", label: "Campaign Attribution",icon: Megaphone },
];

export default function DemandAnalyticsDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [period, setPeriod] = useState("30d");
  const { data, loading, error, refresh } = useAnalytics(activeTab, period);

  return (
    <div className="min-h-screen" style={{ background: T.bg }}>
      {/* Header */}
      <div
        className="border-b px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between"
        style={{ background: T.card, borderColor: T.cardBorder }}
      >
        <div>
          <h1 className="text-xl font-bold" style={{ color: T.charcoal }}>
            Public Demand Intelligence
          </h1>
          <p className="text-sm mt-0.5" style={{ color: T.muted }}>
            Anonymous visitor behaviour · Inquiry funnel · Pre-lead to lead conversion
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <PeriodPicker value={period} onChange={setPeriod} />
          <button
            onClick={refresh}
            className="p-2 rounded-lg border hover:bg-opacity-80 transition-colors"
            style={{ borderColor: T.cardBorder, background: T.card }}
            data-testid="refresh-btn"
            title="Refresh"
          >
            <RefreshCw size={16} style={{ color: T.mid }} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Privacy notice banner */}
      <div
        className="px-6 py-2.5 text-xs flex items-center gap-2 border-b"
        style={{ background: T.goldLight, borderColor: "#FDE68A", color: T.amber }}
      >
        <Filter size={12} />
        Aggregated anonymous data only · No visitor identities · Postal code buckets with fewer than 5 visitors are suppressed · Stop boundary: Lead Created
      </div>

      {/* Tabs */}
      <div
        className="border-b px-6 flex gap-0 overflow-x-auto"
        style={{ background: T.card, borderColor: T.cardBorder }}
      >
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-2 px-4 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors"
            style={activeTab === tab.id
              ? { borderBottomColor: T.ruby, color: T.ruby }
              : { borderBottomColor: "transparent", color: T.mid }}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="px-6 py-6 max-w-7xl mx-auto">
        {error && (
          <div
            className="rounded-xl p-4 mb-6 border text-sm"
            style={{ background: T.rubyLight, borderColor: "#FECACA", color: T.ruby }}
            data-testid="analytics-error"
          >
            {error}
          </div>
        )}

        {loading && !data && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: T.ruby }} />
          </div>
        )}

        {data && (
          <>
            {activeTab === "overview"  && <OverviewTab  data={data} />}
            {activeTab === "geo"       && <GeoTab       data={data} />}
            {activeTab === "funnel"    && <FunnelTab    data={data} />}
            {activeTab === "interests" && <InterestsTab data={data} />}
            {activeTab === "campaigns" && <CampaignsTab data={data} />}
          </>
        )}

        {!loading && !data && !error && (
          <div className="text-center py-20">
            <BarChart2 size={40} className="mx-auto mb-4 opacity-30" />
            <p className="font-medium" style={{ color: T.charcoal }}>No data for this period</p>
            <p className="text-sm mt-1" style={{ color: T.muted }}>
              Analytics data will appear as visitors browse the public portal.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
